/**
 * Handles Appstle subscription widget functionality including:
 * - Price switching between one-time purchase and subscription options
 * - Tooltip display for subscription information
 */
class Appstle {
  constructor() {
    this.originalPrice = null;
    this.init();
  }

  /**
   * Initializes the widget by waiting for required components
   * and setting up event listeners.
   */
  init() {
    Promise.all([
      this.waitForElement('.appstle_subscription_radio_wrapper'),
      customElements.whenDefined('add-to-cart'),
    ]).then(() => {
      this.setupEventListeners();
      this.addTooltip();
    });
  }

  /**
   * Waits for an element to be present in the DOM.
   * @param {string} selector - The CSS selector to wait for
   * @returns {Promise} Resolves when element is found
   */
  waitForElement(selector) {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver((mutations) => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(document.querySelector(selector));
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  /**
   * Creates and adds the tooltip after the subscription radio label
   */
  addTooltip() {
    const tooltipHTML = `
      <div class="appstle-tooltip">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
          <path d="M12 11.5V17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="7.5" r="1" fill="currentColor"/>
        </svg>
        <div class="appstle-tooltip-content">
          <div class="appstle-tooltip-title">Smart replenish</div>
          10% off each order<br>
          Free shipping<br>
          Cancel Anytime
        </div>
      </div>
    `;

    // Find all radio labels and add tooltip
    const radioLabels = document.querySelectorAll(
      '.appstle_subscription_radio_wrapper .appstle_radio_label',
    );
    radioLabels.forEach((label) => {
      // Only add tooltip if it doesn't already exist
      if (!label.nextElementSibling?.classList?.contains('appstle-tooltip')) {
        const tooltipContainer = document.createElement('div');
        tooltipContainer.innerHTML = tooltipHTML.trim();
        const tooltip = tooltipContainer.firstChild;
        label.after(tooltip);
      }
    });
  }

  /**
   * Sets up event listeners for subscription radio changes.
   */
  setupEventListeners() {
    const subscriptionWrapper = document.querySelector('.appstle_subscription_wrapper');
    if (!subscriptionWrapper) return;

    const radioInputs = subscriptionWrapper.querySelectorAll('input[type="radio"]');
    radioInputs.forEach((input) => {
      input.addEventListener('change', () => this.handlePriceUpdate());
    });

    // Set initial price
    this.handlePriceUpdate();
  }

  /**
   * Finds the price element within the add-to-cart form.
   * @param {HTMLElement} addToCartForm - The add-to-cart form element
   * @returns {HTMLElement|null} The price element or null if not found
   */
  findPriceElement(addToCartForm) {
    // First try to find the first direct span before the dot-spacer
    const span = addToCartForm.querySelector('[js-data="price"]');

    return span || null;
  }

  /**
   * Finds the subscription price for the selected subscription option.
   * @returns {string|null} The subscription price or null if not found
   */
  findSubscriptionPrice() {
    const selectedInput = document.querySelector(
      '.appstle_subscription_radio_wrapper input[name="selling_plan"]:checked',
    );
    if (!selectedInput) return null;

    const radioWrapper = selectedInput.closest('.appstle_subscription_radio_wrapper');
    if (!radioWrapper) return null;

    const priceWrapper = radioWrapper.querySelector('.appstle_subscription_amount_wrapper');
    if (!priceWrapper) return null;

    const priceElement = priceWrapper.querySelector('.appstle_subscription_amount:not(.sr-only)');
    return priceElement ? priceElement.textContent.trim() : null;
  }

  /**
   * Updates the price based on the selected purchase option.
   */
  handlePriceUpdate() {
    const addToCartForm = document.querySelector('.product-form__submit');

    if (!addToCartForm) return;

    const priceElement = this.findPriceElement(addToCartForm);
    if (!priceElement) return;

    // Store original price if we haven't yet
    if (!this.originalPrice) {
      this.originalPrice = priceElement.textContent.trim();
    }

    // Check which radio is checked
    const subscriptionInput = document.querySelector('input[name="selling_plan"]:checked');

    // Update price based on selection
    if (!subscriptionInput || subscriptionInput.value === '') {
      priceElement.textContent = this.originalPrice;
    } else {
      const subscriptionPrice = this.findSubscriptionPrice();
      if (subscriptionPrice) {
        priceElement.textContent = subscriptionPrice;
      }
    }
  }
}

// Initialize the widget
new Appstle();

// TODO: Merge this with the Appstle class
document.addEventListener('DOMContentLoaded', function () {
  // Function to set up click listener and dataset observer for appstle widget
  function setupAppstleWidget(appstleSubWidget) {
    // Store the initial selectedSellingPlan value
    let currentSelectedSellingPlan = appstleSubWidget.dataset.selectedSellingPlan;

    // Add click listener
    appstleSubWidget.addEventListener('click', function (e) {
      // Use MutationObserver to watch for dataset changes after click
      const datasetObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-selected-selling-plan'
          ) {
            const newSelectedSellingPlan = appstleSubWidget.dataset.selectedSellingPlan;

            if (newSelectedSellingPlan !== currentSelectedSellingPlan) {
              currentSelectedSellingPlan = newSelectedSellingPlan;

              updateSellingPlan(newSelectedSellingPlan);
            }
          }
        });
      });

      function updateSellingPlan(newSelectedSellingPlan) {
        document.querySelector('#selling_plan').value = newSelectedSellingPlan;
        if (newSelectedSellingPlan !== undefined) {
          document.querySelector('#selling_plan').checked = true;
        } else {
          document.querySelector('#selling_plan').checked = false;
        }
      }

      // Start observing dataset changes
      datasetObserver.observe(appstleSubWidget, {
        attributes: true,
        attributeFilter: ['data-selected-selling-plan'],
      });

      // Stop observing after a reasonable timeout (e.g., 5 seconds)
      setTimeout(() => {
        datasetObserver.disconnect();
      }, 5000);
    });
  }

  // Use MutationObserver to watch for the appstle widget to be injected
  const widgetObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        // Check if the added node is an element
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if it's the appstle widget or contains the appstle widget
          let appstleSubWidget = null;

          if (node.classList && node.classList.contains('appstle_sub_widget')) {
            appstleSubWidget = node;
          } else if (node.querySelector) {
            appstleSubWidget = node.querySelector('.appstle_sub_widget');
          }

          if (appstleSubWidget) {
            // Stop observing since we found the widget
            widgetObserver.disconnect();

            // Set up the widget functionality
            setupAppstleWidget(appstleSubWidget);
          }
        }
      });
    });
  });

  // Check if the widget already exists (in case it loaded before this script)
  const existingWidget = document.querySelector('.appstle_sub_widget');
  if (existingWidget) {
    setupAppstleWidget(existingWidget);
  } else {
    // Start observing for the widget to be added
    widgetObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});
